import { computed, ref, type Ref } from 'vue';
import Swal from 'sweetalert2';
import type { CustomLocationType } from '@/models/address';
import { MAX_INTERMEDIATE_STOPS, MULTI_STOP_WAIT_FEE_WARNING, emptyAddress } from '@/constants/ride';

const LAT_LNG_EPSILON = 1e-6;

const normalize = (value: string | null | undefined) => (value || '').trim().toLowerCase();

const coordsEqual = (a: CustomLocationType, b: CustomLocationType) => {
  if (a.latitude == null || a.longitude == null || b.latitude == null || b.longitude == null) {
    return false;
  }
  return (
    Math.abs(Number(a.latitude) - Number(b.latitude)) < LAT_LNG_EPSILON &&
    Math.abs(Number(a.longitude) - Number(b.longitude)) < LAT_LNG_EPSILON
  );
};

export const areAddressesEqual = (a: CustomLocationType, b: CustomLocationType) => {
  if (coordsEqual(a, b)) return true;

  const street1Equal = normalize(a.streetAddress) === normalize(b.streetAddress) && !!normalize(a.streetAddress);
  if (!street1Equal) return false;

  // If either is an intersection, compare the cross street too
  if (a.type || b.type) {
    if (normalize(a.streetAddress1) !== normalize(b.streetAddress1)) return false;
  }

  return normalize(a.city) === normalize(b.city) && normalize(a.state) === normalize(b.state) && normalize(a.zip) === normalize(b.zip);
};

export type KeyedStop = CustomLocationType & { _key: number };

export function useRideStops() {
  let nextStopKey = 0;
  const stops: Ref<KeyedStop[]> = ref([]);
  const canAddStop = computed(() => stops.value.length < MAX_INTERMEDIATE_STOPS);
  const hasStops = computed(() => stops.value.length > 0);

  const addStop = () => {
    if (!canAddStop.value) return;
    stops.value.push({ ...emptyAddress, _key: nextStopKey++ });

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    Swal.fire({
      title: 'Explain Multi-Stop Wait Fees',
      html: MULTI_STOP_WAIT_FEE_WARNING.split('\n\n')
        .map((p) => `<p>${p}</p>`)
        .join(''),
      icon: 'warning',
      customClass: {
        popup: 'swal2-custom-icon size-default',
        confirmButton: 'btn btn-fill btn-warning',
      },
      buttonsStyling: false,
      allowOutsideClick: false,
      heightAuto: false,
      returnFocus: false,
    });
  };

  const removeStop = (index: number) => {
    stops.value.splice(index, 1);
  };

  const hasDuplicateConsecutiveAddresses = (startAddress: CustomLocationType, endAddress: CustomLocationType) => {
    const nonEmptyStops = stops.value.filter((s) => !!(s.streetAddress && s.streetAddress.trim()));
    const sequence: CustomLocationType[] = [startAddress, ...nonEmptyStops, endAddress];

    for (let i = 0; i < sequence.length - 1; i++) {
      const current = sequence[i];
      const next = sequence[i + 1];
      if (!current.streetAddress || !next.streetAddress) continue;
      if (areAddressesEqual(current, next)) {
        return true;
      }
    }
    return false;
  };

  return {
    stops,
    canAddStop,
    hasStops,
    addStop,
    removeStop,
    hasDuplicateConsecutiveAddresses,
  };
}
